import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecognitionById, useRecognitionMutations, useRecognitionTypes } from '../../hooks/useRecognition';
import { useAuth } from '../../auth/useAuth';
import { useCreateBudgetRequest } from '../../hooks/useBudgetMutations';
import { CreateBudgetRequestRequest } from '../../types';

interface ReviewRecognitionModalProps {
  recognitionId: number;
  onClose: () => void;
  onApprovalSuccess?: (message: string) => void;
}

// Get dollar value for recognition type from the types list
const getDollarValueForType = (typeLabel: string | undefined, types: any[]): number => {
  if (!typeLabel || !types || types.length === 0) return 0;
  // Remove points suffix if present (e.g., "Shine On — 600 pts / $30" -> "Shine On")
  const cleanLabel = typeLabel.split(' — ')[0].split(' - ')[0];
  const matchingType = types.find(t => t.label === cleanLabel);
  return matchingType?.dollarValue || 0;
};

export default function ReviewRecognitionModal({ recognitionId, onClose, onApprovalSuccess }: ReviewRecognitionModalProps) {
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = auth?.roles?.includes('BRG.Admin') || false;
  const { data: recognition, isLoading } = useRecognitionById(recognitionId);
  const { data: types } = useRecognitionTypes();
  const { approveRecognition, denyRecognition } = useRecognitionMutations();
  const createBudgetMutation = useCreateBudgetRequest();

  const [isApproving, setIsApproving] = useState(false);
  const [isDenying, setIsDenying] = useState(false);
  const [budgetCreationError, setBudgetCreationError] = useState<string | null>(null);

  const handleApprove = async () => {
    setIsApproving(true);
    setBudgetCreationError(null);
    try {
      // Approve the recognition
      await approveRecognition.mutateAsync(recognitionId);
      
      // Auto-create budget entry
      // Use dollarValueSnapshot from recognition, or parse from type label if not available
      const dollarValue = recognition?.dollarValueSnapshot !== undefined 
        ? recognition.dollarValueSnapshot 
        : getDollarValueForType(recognition?.recognitionTypeLabel, types || []);
      
      // Clean up recognition type label (remove points/dollar formatting if present)
      const cleanTypeLabel = recognition?.recognitionTypeLabel?.split(' — ')[0] || 'Recognition';
      
      // Parse the anticipatedAwardDate - it might be in format "YYYY-MM-DD" already
      let scheduledDateFormatted: string | undefined = undefined;
      if (recognition?.anticipatedAwardDate) {
        // Recognition date is typically in "YYYY-MM-DD" format already
        scheduledDateFormatted = recognition.anticipatedAwardDate;
      }
      
      const budgetRequest: CreateBudgetRequestRequest = {
        brgId: recognition?.brgId || 0,
        fiscalYear: `FY ${new Date().getFullYear()}`, // This can be adjusted based on actual fiscal year logic
        projectEvent: recognition?.eventTitle || '',
        purposeDescription: `${recognition?.recipientName} — ${cleanTypeLabel} Recognition`,
        requestDescription: `${recognition?.recipientName} recognized as ${cleanTypeLabel}. ${recognition?.reason || ''}`,
        requestAmount: dollarValue,
        scheduledDate: scheduledDateFormatted,
        budgetStatusId: 2, // 2 = Approved status from MDM_BudgetStatuses
        mmRecognitionId: recognition?.recognitionId,
      };

      // Log request for debugging
      // console.log('Creating budget entry with request:', budgetRequest);
      // console.log('Recognition data:', {
      //   recognitionId: recognition?.recognitionId,
      //   brgId: recognition?.brgId,
      //   recipientName: recognition?.recipientName,
      //   eventTitle: recognition?.eventTitle,
      //   dollarValue: recognition?.dollarValueSnapshot,
      //   anticipatedAwardDate: recognition?.anticipatedAwardDate,
      // });

      try {
        const budgetResponse = await createBudgetMutation.mutateAsync(budgetRequest);
        if (budgetResponse && budgetResponse.budgetRequestId) {
          // Budget created successfully - recognition will auto-populate on the dashboard
        }
      } catch (budgetError: any) {
        // Log full error details for debugging
        console.error('Budget creation full error:', budgetError);
        if (budgetError.response) {
          console.error('Error response data:', budgetError.response.data);
        }
        // Don't fail the approval
        setBudgetCreationError('Budget Tracking dashboard sync failed and will be retried automatically.');
      }

      if (onApprovalSuccess) {
        // Include budget error in the success message if it occurred
        const finalMessage = budgetCreationError 
          ? `Recognition request was approved. ${budgetCreationError}`
          : 'Recognition request was approved.';
        onApprovalSuccess(finalMessage);
      }
    } catch (error: any) {
      console.error('Failed to approve:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleDeny = async () => {
    setIsDenying(true);
    try {
      await denyRecognition.mutateAsync(recognitionId);
      if (onApprovalSuccess) {
        onApprovalSuccess('Recognition request was denied.');
      }
    } catch (error: any) {
      console.error('Failed to deny:', error);
    } finally {
      setIsDenying(false);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div style={{ color: 'white' }}>Loading...</div>
      </div>
    );
  }

  if (!recognition) {
    return null;
  }

  const isPending = recognition.recognitionStatusId === 1;
  const isApproved = recognition.recognitionStatusId === 2;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#1a3bcc',
            color: 'white',
            padding: '14px 18px',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 0,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 800 }}>Recognition Review</h2>
            <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '2px' }}>Review and approve or deny this request</div>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 4px',
              lineHeight: 1,
              fontWeight: 'bold',
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>

          {/* Notice Banner */}
          <div style={{ backgroundColor: '#f0f4ff', borderLeft: '4px solid #1a3bcc', padding: '10px 14px', fontSize: '11px', color: '#333', marginBottom: '14px', borderRadius: '0 4px 4px 0' }}>
            <strong style={{ color: '#1a3bcc' }}>Submission Notice:</strong> Recognition requests are reviewed by the BRG Admin. Approved requests automatically create an entry in the Budget Tracking dashboard.
          </div>

          {/* Budget Creation Error Banner (if exists) */}
          {budgetCreationError && (
            <div style={{ backgroundColor: '#fff3cd', borderLeft: '4px solid #b27800', padding: '10px 14px', fontSize: '11px', color: '#333', marginBottom: '14px', borderRadius: '0 4px 4px 0' }}>
              <strong style={{ color: '#b27800' }}>Note:</strong> {budgetCreationError}
            </div>
          )}

          {/* Recipient Name */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Recipient Name
            </label>
            <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222' }}>
              {recognition.recipientName}
            </div>
          </div>

          {/* BRG & Event - Row */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
                BRG
              </label>
              <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222' }}>
                {recognition.brgName}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
                Linked BRG Event
              </label>
              <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222' }}>
                {recognition.eventTitle || '—'}
              </div>
            </div>
          </div>

          {/* Type & Points - Row */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
                Recognition Type
              </label>
              <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222' }}>
                {recognition.recognitionTypeLabel ? recognition.recognitionTypeLabel.split(' - ')[0] : '—'}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
                Points / Dollar Value
              </label>
              <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222' }}>
                {recognition.pointsSnapshot ? recognition.pointsSnapshot.toLocaleString() : '0'} pts / ${recognition.dollarValueSnapshot || '0'}
              </div>
            </div>
          </div>

          {/* Award Date */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Anticipated Award Date
            </label>
            <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222' }}>
              {recognition.anticipatedAwardDate ? (() => {
                const [year, month, day] = recognition.anticipatedAwardDate.split('-');
                return `${month}/${day}/${year.slice(-2)}`;
              })() : '—'}
            </div>
          </div>

          {/* Submitted By */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Submitted By
            </label>
            <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222' }}>
              {recognition.submittedByName}
            </div>
          </div>

          {/* Reason */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Reason for Recognition
            </label>
            <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222', minHeight: '50px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {recognition.reason}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Recognition Description
            </label>
            <div style={{ backgroundColor: '#f8f9fb', border: '1px solid #ddd', borderRadius: '4px', padding: '8px 12px', fontSize: '12px', color: '#222', minHeight: '60px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {recognition.description || '(No description provided)'}
            </div>
          </div>

          {/* Linked BRG Budget */}
          {isApproved && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
                Linked BRG Budget
              </label>
              <button
                onClick={() => {
                  navigate(`/budget-dashboard?highlightRecognition=${recognition.recognitionId}`);
                  onClose();
                }}
                style={{
                  backgroundColor: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#1a3bcc',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#f0f2ff';
                }}
                onMouseOut={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fb';
                }}
              >
                View on Budget Page →
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', paddingTop: '14px', borderTop: '1px solid #eee', justifyContent: isAdmin && isPending ? 'flex-end' : 'space-between', alignItems: 'center' }}>
            {!isPending && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 700,
                  backgroundColor: isApproved ? '#d4edda' : '#f8d7da',
                  color: isApproved ? '#1a7c3a' : '#a01a23',
                }}
              >
                <span>{isApproved ? '✓' : '✕'}</span>
                <span>This request has been {isApproved ? 'Approved' : 'Denied'}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              {isAdmin && isPending && (
                <>
                  <button
                    onClick={handleDeny}
                    disabled={isDenying}
                    style={{
                      backgroundColor: '#a01a23',
                      color: 'white',
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isDenying ? 'not-allowed' : 'pointer',
                      fontWeight: 700,
                      fontSize: '12px',
                      opacity: isDenying ? 0.6 : 1,
                    }}
                  >
                    {isDenying ? 'Denying...' : 'Deny'}
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={isApproving}
                    style={{
                      backgroundColor: '#1a7c3a',
                      color: 'white',
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isApproving ? 'not-allowed' : 'pointer',
                      fontWeight: 700,
                      fontSize: '12px',
                      opacity: isApproving ? 0.6 : 1,
                    }}
                  >
                    {isApproving ? 'Approving...' : 'Approve'}
                  </button>
                </>
              )}
              {!isPending && (
                <button
                  onClick={onClose}
                  style={{
                    backgroundColor: '#f0f0f0',
                    color: '#333',
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '12px',
                  }}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
